import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useMallarFetch } from '@/hooks/useMallarFetch'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Loader } from '@/components/Loader'

// Admin-only: flip the Mallar payments toggle live (persisted server-side in app_settings).
export default function MallarPaymentsAdmin() {
  const apiFetch = useMallarFetch()
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    apiFetch('/api/admin/payments-config')
      .then(r => r.json())
      .then(d => setEnabled(!!d.enabled))
      .catch(() => { toast.error('Kunde inte hämta betalningsinställning.'); setEnabled(true) })
  }, [])

  const toggle = async (next: boolean) => {
    if (saving) return
    const prev = enabled
    setSaving(true)
    setEnabled(next) // optimistic
    try {
      const res = await apiFetch('/api/admin/payments-config', {
        method: 'PUT',
        body: JSON.stringify({ enabled: next }),
      })
      if (!res.ok) throw new Error(String(res.status))
      const d = await res.json()
      setEnabled(!!d.enabled)
      toast.success(next
        ? 'Betalningar påslagna – mallar kräver köp.'
        : 'Betalningar avstängda – mallar är gratis.')
    } catch {
      setEnabled(prev) // revert
      toast.error('Kunde inte uppdatera inställningen.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="w-full">
      <Card>
        <CardHeader>
          <CardTitle>Avtalsmallar – betalningar</CardTitle>
          <CardDescription>
            När påslaget måste användare köpa en mall (99 kr) innan de kan skapa ett utkast.
            När avstängt kan alla registrerade användare skapa, redigera och ladda ner avtal gratis.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {enabled === null ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader className="h-4 w-4" /> Laddar…
            </div>
          ) : (
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <p className="text-sm font-medium">Kräv betalning för mallar</p>
                <p className="text-xs text-muted-foreground">
                  {enabled ? 'På – mallar kräver köp' : 'Av – mallar är gratis'}
                </p>
              </div>
              <Switch checked={enabled} onCheckedChange={toggle} disabled={saving} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
